import type { Quest as QuestType } from 'types/quests'
import { lightweightThreadInStreamEnabled } from 'lib/lightweightThreadInStream'
import { streamReplyCardsEnabled } from 'lib/streamCards'
import { isDirectMessageHeadAnswer, isDirectMessageRoom } from 'lib/directMessages'

const QUEST_PREVIEW_ANSWER_COUNT = 3

function createdAtValue(value: unknown): number | null {
    if (value == null) return null

    const numeric = Number(value)
    if (Number.isFinite(numeric)) return numeric

    const parsed = Date.parse(String(value))
    return Number.isFinite(parsed) ? parsed : null
}

function compareQuestCardCreatedAt(left: any, right: any) {
    return (createdAtValue(left?.created_at) || 0) - (createdAtValue(right?.created_at) || 0)
}

function compareQuestCardPosition(left: any, right: any) {
    return Number(left?.position || 0) - Number(right?.position || 0)
}

function normalizedAnswerId(answer: any): string | null {
    return answer?.id == null ? null : String(answer.id)
}

function normalizedAnswerIds(value: unknown): string[] {
    return Array.isArray(value)
        ? value.map(entry => (entry == null ? null : String(entry))).filter(Boolean)
        : []
}

export function resolveQuestStreamVisibleAnswerCount(quest: QuestType | null | undefined): number {
    const sortedAnswers = Array.isArray(quest?.sorted_answers) ? quest.sorted_answers : []
    return sortedAnswers.filter(answer => answer && !answer.archive).length
}

function compareQuestCardVote(left: any, right: any) {
    const leftUpvalue = Number(left?.bsv_upvalue_amt || 0)
    const rightUpvalue = Number(right?.bsv_upvalue_amt || 0)
    if (leftUpvalue !== rightUpvalue) return rightUpvalue - leftUpvalue

    const leftVote = Number(left?.vote_total || 0)
    const rightVote = Number(right?.vote_total || 0)
    const voteOrder = rightVote - leftVote
    return voteOrder === 0 ? compareQuestCardPosition(left, right) : voteOrder
}

function resolveQuestStreamSortedVisibleAnswers(quest: QuestType | null | undefined): any[] {
    const sortedAnswers = Array.isArray(quest?.sorted_answers) ? quest.sorted_answers : []
    const visibleAnswers = sortedAnswers.filter(answer => answer && !answer.archive)
    const sortColumn = String(quest?.default_sort_col || 'position')
    const sortDirection = String(quest?.default_sort_dir || 'asc')

    if (sortColumn === 'position') {
        const byPosition = visibleAnswers.slice().sort(compareQuestCardPosition)
        return sortDirection === 'desc' ? byPosition.reverse() : byPosition
    }

    const byVote = visibleAnswers.slice().sort(compareQuestCardVote)
    return sortDirection === 'asc' ? byVote.reverse() : byVote
}

function answerCreatedAtAtOrAfter(answer: any, threshold: unknown): boolean {
    if (threshold == null) return false

    const answerTime = createdAtValue(answer?.created_at)
    const thresholdTime = createdAtValue(threshold)
    if (answerTime != null && thresholdTime != null) return answerTime >= thresholdTime

    return String(answer?.created_at || '') >= String(threshold || '')
}

function currentViewerId(): unknown {
    return typeof gon === 'undefined' ? null : gon?.currentUser?.id
}

function answerBelongsToViewer(answer: any, viewerId: unknown): boolean {
    if (viewerId == null) return false

    const answerUserId = answer?.user_id ?? answer?.user?.id
    return answerUserId != null && String(answerUserId) === String(viewerId)
}

// The unseen-row indicator shares the slot resolver's own new-since-visit predicate, so
// a row wears the marker exactly when this file's precedence order counts it as new.
export function answerIsNewSinceQuestVisit(
    answer: any,
    quest: Partial<QuestType> | null | undefined,
    viewerId: unknown = currentViewerId(),
): boolean {
    if (!answer) return false
    if (answerBelongsToViewer(answer, viewerId)) return false

    if (quest && Object.prototype.hasOwnProperty.call(quest, 'viewer_last_visited_at')) {
        if (quest.viewer_last_visited_at == null) return true
        return answerCreatedAtAtOrAfter(answer, quest.viewer_last_visited_at)
    }

    // Rolling-deploy fallback for payloads from servers that predate viewer visit context.
    return answerCreatedAtAtOrAfter(answer, quest?.last_action_created_at)
}

// stream_reply_cards value rows: the resting card shows the thread's proof of quality
// instead of a bare title — the top two replies when both earned upvalue, one when
// only one did, the single top-voted reply when none have sats yet. Value ranking
// selects the rows; the selected thin replies retain chronological presentation.
// The slot precedence reads: search match → new-since-visit → top value → bare.
export function resolveQuestStreamValueAnswers(visibleAnswers: any[]): any[] {
    if (!Array.isArray(visibleAnswers) || visibleAnswers.length === 0) return []

    const byValue = visibleAnswers.slice().sort(compareQuestCardVote)
    const upvalued = byValue.filter(answer => Number(answer?.bsv_upvalue_amt || 0) > 0)
    if (upvalued.length > 0) {
        return upvalued.slice(0, 2).sort(compareQuestCardCreatedAt)
    }

    return [byValue[0]]
}

export function resolveQuestStreamPreviewAnswers(quest: QuestType | null | undefined): any[] {
    const visibleAnswers = resolveQuestStreamSortedVisibleAnswers(quest)
    const matchingAnswerIds = normalizedAnswerIds(quest?.matching_answer_ids)

    if (matchingAnswerIds.length > 0) {
        return visibleAnswers
            .filter(answer => {
                const id = normalizedAnswerId(answer)
                return !!id && matchingAnswerIds.includes(id)
            })
            .sort((left, right) => {
                const leftId = normalizedAnswerId(left)
                const rightId = normalizedAnswerId(right)
                return (
                    matchingAnswerIds.indexOf(leftId || '') -
                    matchingAnswerIds.indexOf(rightId || '')
                )
            })
    }

    const hasViewerVisitContext = Object.prototype.hasOwnProperty.call(
        quest || {},
        'viewer_last_visited_at',
    )
    const lastActionPreviewAnswers =
        hasViewerVisitContext && quest?.viewer_last_visited_at == null
            ? []
            : visibleAnswers.filter(answer => answerIsNewSinceQuestVisit(answer, quest))
    if (lastActionPreviewAnswers.length > 0) return lastActionPreviewAnswers

    // Lightweight cards drop the "latest 3" filler: previews render only when they carry
    // a signal (search matches or new-since-last-visit above). Behind stream_reply_cards the
    // resting state falls through to the top-value rows instead of going bare.
    if (lightweightThreadInStreamEnabled()) {
        if (streamReplyCardsEnabled()) return resolveQuestStreamValueAnswers(visibleAnswers)
        return []
    }

    return visibleAnswers.slice(Math.max(0, visibleAnswers.length - QUEST_PREVIEW_ANSWER_COUNT))
}

// A direct-message conversation's stream card is its latest message, in the
// reply-card anatomy (Answer's postLens), so the Messages list reads like an
// inbox. The head (the letter strip) stands in only until there is a message.
export function resolveDirectMessageLatestAnswer(quest: QuestType | null | undefined): any | null {
    if (!isDirectMessageRoom(quest as any)) return null

    const messages = resolveQuestStreamSortedVisibleAnswers(quest).filter(
        answer => !answer.is_draft && !isDirectMessageHeadAnswer(answer, quest as any),
    )
    if (messages.length === 0) return null

    const declaredIds = normalizedAnswerIds(quest?.sorted_answer_ids)
    const lastDeclaredId = declaredIds[declaredIds.length - 1]
    const byCreatedAt = messages.slice().sort(compareQuestCardCreatedAt)
    return (
        messages.find(answer => normalizedAnswerId(answer) === lastDeclaredId) ||
        byCreatedAt[byCreatedAt.length - 1]
    )
}

export function resolveQuestStreamRenderedAnswers(quest: QuestType | null | undefined): any[] {
    if (!quest) return []

    const latestDirectMessage = resolveDirectMessageLatestAnswer(quest)
    if (latestDirectMessage) return [latestDirectMessage]

    const previewAnswers = resolveQuestStreamPreviewAnswers(quest)
    if (quest.is_system && !quest.parent?.leaderboard_announcement) {
        const hasMatchingAnswers = normalizedAnswerIds(quest?.matching_answer_ids).length > 0
        if (hasMatchingAnswers) return [previewAnswers[0]].filter(Boolean)

        const defaultPreviewAnswers = resolveQuestStreamSortedVisibleAnswers(quest)
            .slice()
            .sort(compareQuestCardCreatedAt)
            .slice(
                Math.max(
                    0,
                    resolveQuestStreamVisibleAnswerCount(quest) - QUEST_PREVIEW_ANSWER_COUNT,
                ),
                resolveQuestStreamVisibleAnswerCount(quest),
            )
            .sort(compareQuestCardPosition)
        return [defaultPreviewAnswers[0] || previewAnswers[0]].filter(Boolean)
    }

    return [quest.parent, ...previewAnswers].filter(Boolean)
}
